import * as React from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { Upload, X, File, Plus } from "lucide-react";
import { GetSubmissionsOutput } from "server/StartupAdmins CRUD/OutputsTypes";
import { exerciseTitle } from "components/common/utils";
import { createProblemConversation } from "server/Conversations CRUD/Requests";
import { CreateProblemConversationInput } from "server/Conversations CRUD/InputsTypes";

interface SubmitCorrectionModalProps {
  open: boolean;
  onClose: () => void;
  submission: GetSubmissionsOutput;
}

const SubmitCorrectionModal: React.FC<SubmitCorrectionModalProps> = ({
  open,
  onClose,
  submission,
}) => {
  const [comments, setComments] = React.useState("");
  const [files, setFiles] = React.useState<File[]>([]);
  const [loading, setLoading] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      // Ajouter les nouveaux fichiers aux fichiers existants
      setFiles((prevFiles) => [
        ...prevFiles,
        ...Array.from(event.target.files as FileList),
      ]);

      // Réinitialiser l'input file pour permettre la sélection des mêmes fichiers
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (event.dataTransfer.files) {
      // Ajouter les fichiers déposés aux fichiers existants
      setFiles((prevFiles) => [
        ...prevFiles,
        ...Array.from(event.dataTransfer.files),
      ]);
    }
  };

  const removeFile = (index: number) => {
    setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  const addMoreFiles = () => {
    fileInputRef.current?.click();
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);

      const input: CreateProblemConversationInput = {
        body: comments,
        exerciseId: submission._id,
        files: files,
      };

      const response = await createProblemConversation(input);

      if (response.status === 200) {
        console.log(response);
        setComments("");
        setFiles([]);
        onClose();
      } else {
        throw new Error("Erreur lors de la soumission des corrections");
      }
    } catch (error) {
      console.error("Erreur lors de la soumission des corrections:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="submit-correction-title"
      sx={{ zIndex: 9999 }}
    >
      <DialogTitle id="submit-correction-title">
        Soumettre des fichiers de correction
      </DialogTitle>
      <DialogContent>
        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: "bold" }}>
          {exerciseTitle(submission)}
        </Typography>

        <Typography variant="subtitle2" sx={{ mb: 2 }}>
          Fichiers de correction
        </Typography>

        <Box
          sx={{
            border: "2px dashed #e0e0e0",
            borderRadius: 2,
            p: 3,
            mb: 3,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            "&:hover": {
              borderColor: "#c0c0c0",
              bgcolor: "#f9f9f9",
            },
          }}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={handleFileChange}
            multiple
          />
          <Upload size={40} color="#9e9e9e" />
          <Typography sx={{ mt: 2, color: "#616161" }}>
            Cliquez ou glissez-déposez les fichiers
          </Typography>
          <Typography variant="body2" sx={{ color: "#9e9e9e" }}>
            PDF, Word, Excel, Images, etc.
          </Typography>
        </Box>

        {files.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
              }}
            >
              <Typography variant="subtitle2">
                Fichiers sélectionnés ({files.length})
              </Typography>
              <Button
                startIcon={<Plus size={16} />}
                size="small"
                onClick={addMoreFiles}
                variant="outlined"
                sx={{ py: 0.5 }}
              >
                Ajouter plus
              </Button>
            </Box>
            <Box
              sx={{
                maxHeight: files.length > 5 ? "200px" : "auto",
                overflowY: files.length > 5 ? "auto" : "visible",
                bgcolor: "#f5f5f5",
                p: 1,
                borderRadius: 1,
              }}
            >
              {files.map((file, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: 1,
                    mb: 0.5,
                    bgcolor: "white",
                    borderRadius: 1,
                    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      flexGrow: 1,
                      maxWidth: "calc(100% - 40px)",
                    }}
                  >
                    <File size={16} color="#5c6bc0" />
                    <Typography
                      variant="body2"
                      sx={{
                        ml: 1,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {file.name}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ ml: 1, color: "#9e9e9e", flexShrink: 0 }}
                    >
                      ({(file.size / 1024).toFixed(1)} KB)
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    sx={{ minWidth: "auto", p: 0.5 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFile(index);
                    }}
                  >
                    <X size={16} color="#757575" />
                  </Button>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Commentaires
        </Typography>
        <TextField
          multiline
          rows={4}
          fullWidth
          placeholder="Ajoutez vos commentaires sur la correction ici..."
          value={comments}
          onChange={(e) => setComments(e.target.value)}
          variant="outlined"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Annuler
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          sx={{
            backgroundColor: "#E3B500",
            color: "#fff",
            "&:hover": {
              backgroundColor: "#C99A00",
            },
            "&:disabled": {
              backgroundColor: "#F0D678",
              color: "#fff",
            },
          }}
          disabled={loading || (files.length === 0 && comments.trim() === "")}
        >
          {loading ? "Envoi en cours..." : "Soumettre la correction"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SubmitCorrectionModal;
